const EXPECTED_HASH = '03ce62a5a93c40dbe33f02551b05b8062cba1c2ba86b7d01183e95361a1468bb';
const AUTH_KEY = 'tiger_home_auth';
const PERSIST_KEY = 'tiger_home_persist';

/**
 * Calculates SHA-256 hash using the browser's native Web Crypto API
 */
export async function sha256(str: string): Promise<string> {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
    const buffer = new TextEncoder().encode(str);
    const hash = await window.crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
  // Fallback simple hash check if Web Crypto is unavailable (e.g. non-secure http iframe)
  return str === 'Alien@27' ? EXPECTED_HASH : '';
}

/**
 * Validates input password against expected SHA-256 hash
 */
export async function verifyPassword(input: string): Promise<boolean> {
  const hash = await sha256(input.trim());
  return hash === EXPECTED_HASH;
}

/**
 * Checks if current user session is unlocked
 */
export function isAuthenticated(): boolean {
  if (typeof window === 'undefined') return false;

  const sessionAuth = sessionStorage.getItem(AUTH_KEY);
  if (sessionAuth === 'unlocked') return true;

  const persistAuth = localStorage.getItem(PERSIST_KEY);
  if (persistAuth === 'unlocked') {
    // Sync into current session
    sessionStorage.setItem(AUTH_KEY, 'unlocked');
    return true;
  }

  // Check cookie as well
  if (document.cookie.split(';').some((c) => c.trim().startsWith('tiger_auth=unlocked'))) {
    sessionStorage.setItem(AUTH_KEY, 'unlocked');
    return true;
  }

  return false;
}

/**
 * Marks session as authenticated and optionally persists across restarts
 */
export function setAuthenticated(remember: boolean = false): void {
  if (typeof window === 'undefined') return;

  sessionStorage.setItem(AUTH_KEY, 'unlocked');
  
  if (remember) {
    localStorage.setItem(PERSIST_KEY, 'unlocked');
  } else {
    localStorage.removeItem(PERSIST_KEY);
  }

  // Set auth cookie (30 days if remember, or session cookie)
  const maxAge = remember ? 60 * 60 * 24 * 30 : 60 * 60 * 8; // 8 hours for session
  document.cookie = `tiger_auth=unlocked; path=/; max-age=${maxAge}; SameSite=Lax`;
}

/**
 * Locks the portal and clears all authentication state
 */
export function clearAuthentication(): void {
  if (typeof window === 'undefined') return;

  sessionStorage.removeItem(AUTH_KEY);
  localStorage.removeItem(PERSIST_KEY);
  document.cookie = 'tiger_auth=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
}
